import React, { useState } from 'react';
import { Typography, Icon, Fab, Menu, MenuItem, ListItemIcon, ListItemText } from '@material-ui/core';
import { KeyboardArrowDown } from '@material-ui/icons';
import { FuseAnimate } from '@fuse';
import { getLogoFilename } from 'app/main/util/provider';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
	clusterActionButton: {
		backgroundColor: theme.palette.background.paper,
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.spacing(1),
		padding: theme.spacing(1.5, 2),
		maxHeight: '56px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		cursor: 'pointer',
		transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'transform']),
		boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
		'&:hover': {
			backgroundColor: '#FCFCFC',
			borderColor: theme.palette.primary.main,
			boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
			transform: 'translateY(-1px)',
		},
		'&:focus': {
			outline: 'none',
			borderColor: theme.palette.primary.main,
			boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`,
		},
		'&:disabled': {
			cursor: 'not-allowed',
			opacity: 0.6,
			'&:hover': {
				backgroundColor: theme.palette.background.paper,
				borderColor: theme.palette.divider,
				boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
				transform: 'none',
			}
		}
	},
	clusterActionText: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		flex: 1,
	},
	clusterActionTitle: {
		fontSize: '1.2rem',
		fontWeight: 600,
		color: theme.palette.text.primary,
		lineHeight: 1.2,
	},
	clusterActionSubtitle: {
		fontSize: '0.9rem',
		color: theme.palette.text.secondary,
		marginTop: '2px',
		lineHeight: 1.2,
		maxWidth: '180px',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	clusterDropdownButton: {
		backgroundColor: theme.palette.background.paper,
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.spacing(1),
		padding: theme.spacing(1, 1.5),
		height: '48px',
		display: 'flex',
		alignItems: 'center',
		cursor: 'pointer',
		transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow']),
		boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
		'&:hover': {
			backgroundColor: '#FCFCFC',
			borderColor: theme.palette.primary.main,
			boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
		},
		'&:focus': {
			outline: 'none',
			borderColor: theme.palette.primary.main,
			boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`,
		}
	},
	darkClusterActionButton: {
		backgroundColor: '#424242',
		border: `1px solid rgba(255, 255, 255, 0.12)`,
		borderRadius: theme.spacing(1),
		padding: theme.spacing(1.5, 2),
		maxHeight: '56px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		cursor: 'pointer',
		transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'transform']),
		boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
		'&:hover': {
			backgroundColor: '#515151',
			borderColor: theme.palette.primary.main,
			boxShadow: '0 6px 24px rgba(0, 0, 0, 0.4)',
			transform: 'translateY(-1px)',
		},
		'&:focus': {
			outline: 'none',
			borderColor: theme.palette.primary.main,
			boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`,
		},
		'&:disabled': {
			cursor: 'not-allowed',
			opacity: 0.6,
			'&:hover': {
				backgroundColor: '#424242',
				borderColor: 'rgba(255, 255, 255, 0.12)',
				boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
				transform: 'none',
			}
		},
		'& .MuiTypography-root': {
			color: '#ffffff',
		},
		'& .MuiIcon-root': {
			color: '#ffffff',
		}
	}
}));

const ClusterHeader = ({ 
	cluster, 
	clusters,
	providers, 
	loading, 
	burstingText, 
	handleClusterActionMenuOpen,
	handleChangeCluster,
	refresh 
}) => {
	const classes = useStyles();
	const [clusterDropdownAnchor, setClusterDropdownAnchor] = useState(null);

	const handleClusterDropdownOpen = (event) => {
		setClusterDropdownAnchor(event.currentTarget);
	};

	const handleClusterDropdownClose = () => {
		setClusterDropdownAnchor(null);
	};

	const handleClusterSelect = (clusterId) => {
		if (handleChangeCluster) {
			handleChangeCluster({ target: { value: clusterId } });
		}
		handleClusterDropdownClose();
	};

	// Filter to only show available clusters
	const availableClusters = clusters ? clusters.filter(c => c.info?.state === 'available') : [];
	const showDropdown = availableClusters.length > 1;

	return (
		<div className="flex flex-1 w-full items-center justify-between px-40">
			<div className="flex flex-col items-start max-w-full">
				<Typography
					id="cluster_view_back-btn"
					className="normal-case flex items-center sm:mb-12 cursor-pointer"
					role="button"
					color="textPrimary"
					onClick={() => window.history.back()}
				>
					<Icon className="mr-4 text-20">arrow_back</Icon>
					Back
				</Typography>
				{cluster && (
					<div id="cluster_view_provider-img" className="flex items-center max-w-full">
						<img
							className="w-32 sm:w-48 mr-8 sm:mr-16 rounded"
							src={`assets/images/providers/${getLogoFilename(cluster.provider)}`}
							alt="Provider Logo"
						/>

						<div className="flex flex-col min-w-0">
							<div 
								className="flex items-center cursor-pointer"
								onClick={showDropdown ? handleClusterDropdownOpen : undefined}
								role={showDropdown ? "button" : undefined}
								tabIndex={showDropdown ? 0 : undefined}
								onKeyPress={(e) => e.key === 'Enter' && showDropdown && handleClusterDropdownOpen(e)}
								style={{
									padding: showDropdown ? '4px 8px 4px 0' : '0',
									borderRadius: '4px',
									transition: 'background-color 0.2s',
								}}
								onMouseEnter={(e) => {
									if (showDropdown) {
										e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.08)';
									}
								}}
								onMouseLeave={(e) => {
									if (showDropdown) {
										e.target.style.backgroundColor = 'transparent';
									}
								}}
							>
								<Typography id="cluster_view_cluster-name" className="text-16 sm:text-20 truncate">
									{`${cluster.name}`}
								</Typography>
								{showDropdown && (
									<KeyboardArrowDown style={{ marginLeft: '8px', fontSize: '20px' }} />
								)}
							</div>
							<Typography id="cluster_view_provider-name" variant="caption">
								{providers && providers.find(n => cluster.provider === n.id)?.name}
							</Typography>
						</div>
						{showDropdown && (
							<Menu
								anchorEl={clusterDropdownAnchor}
								open={Boolean(clusterDropdownAnchor)}
								onClose={handleClusterDropdownClose}
								transformOrigin={{
									vertical: 'top',
									horizontal: 'left',
								}}
								anchorOrigin={{
									vertical: 'bottom',
									horizontal: 'left',
								}}
							>
								{availableClusters.map((clusterItem) => (
									<MenuItem
										key={clusterItem.id}
										onClick={() => handleClusterSelect(clusterItem.id)}
										selected={cluster && cluster.id === clusterItem.id}
									>
										<ListItemIcon>
											<img
												style={{ width: '24px', height: '24px', borderRadius: '4px' }}
												src={`assets/images/providers/${getLogoFilename(clusterItem.provider)}`}
												alt="Provider Logo"
											/>
										</ListItemIcon>
										<ListItemText primary={clusterItem.name} />
									</MenuItem>
								))}
							</Menu>
						)}
					</div>
				)}
			</div>
			<div className="flex justify-between items-center w-1/4">
				<div className="w-2/3">
					<div
						className={classes.darkClusterActionButton}
						onClick={cluster && !loading ? handleClusterActionMenuOpen : undefined}
						style={{
							cursor: cluster && !loading ? 'pointer' : 'not-allowed',
							opacity: cluster && !loading ? 1 : 0.6
						}}
						role="button"
						tabIndex={0}
						onKeyPress={(e) => e.key === 'Enter' && cluster && !loading && handleClusterActionMenuOpen(e)}
					>
						<div className={classes.clusterActionText}>
							<Typography className={classes.clusterActionTitle}>
								{loading ? 'Loading...' : cluster ? 'Cluster Actions' : 'No Cluster Selected'}
							</Typography>
							<Typography className={classes.clusterActionSubtitle} title={cluster?.name || 'Select a cluster to view actions'}>
								{loading ? 'Please wait...' : cluster ? `${cluster.name} • ${cluster.info?.state}` : 'Select a cluster to view actions'}
							</Typography>
						</div>
						<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							{cluster && cluster.info?.state === 'available' && (
								<div 
									style={{ 
										width: '8px', 
										height: '8px', 
										borderRadius: '50%', 
										backgroundColor: '#4caf50',
										animation: 'pulse 2s infinite'
									}} 
								/>
							)}
							{cluster && cluster.info?.state === 'deploying' && (
								<div 
									style={{ 
										width: '8px', 
										height: '8px', 
										borderRadius: '50%', 
										backgroundColor: '#ff9800',
										animation: 'pulse 2s infinite'
									}} 
								/>
							)}
							{cluster && ['failed', 'destroyed'].includes(cluster.info?.state) && (
								<div 
									style={{ 
										width: '8px', 
										height: '8px', 
										borderRadius: '50%', 
										backgroundColor: '#f44336'
									}} 
								/>
							)}
							<Icon color={cluster && !loading ? 'primary' : 'disabled'}>
								more_vert
							</Icon>
						</div>
					</div>
				</div>
				<Fab id="cluster_view_refresh-btn" onClick={() => refresh()} color="secondary" size="small" aria-label="refresh">
					<Icon>refresh</Icon>
				</Fab>
			</div>
		</div>
	);
};
export default ClusterHeader;

